import { useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark';
// New key deliberately does not activate unfinished legacy account preferences.
export const THEME_STORAGE_KEY = 'botspot.websiteThemeMode';
const THEME_CHANGE_EVENT = 'botspot:theme-mode-change';
let volatileMode: ThemeMode = 'dark';
let volatileOverride: ThemeMode | null = null;

export function applyThemeMode(mode: string): ThemeMode {
  return mode === 'light' ? 'light' : 'dark';
}

export function readThemeMode(): ThemeMode {
  if (!globalThis.window) return 'dark';
  if (volatileOverride) return volatileOverride;
  try {
    return applyThemeMode(window.localStorage.getItem(THEME_STORAGE_KEY) || 'dark');
  } catch {
    return volatileMode;
  }
}

export function syncDocumentTheme(): void {
  if (!globalThis.document) return;
  const mode = readThemeMode();
  document.documentElement.dataset.bsTheme = mode;
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
}

function subscribe(onChange: () => void): () => void {
  const update = () => {
    syncDocumentTheme();
    onChange();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
    volatileOverride = null;
    update();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(THEME_CHANGE_EVENT, update);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(THEME_CHANGE_EVENT, update);
  };
}

function setThemeMode(nextMode: string): void {
  volatileMode = applyThemeMode(nextMode);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, volatileMode);
    volatileOverride = null;
  } catch {
    // Keep the toggle usable when browser storage is disabled or full.
    volatileOverride = volatileMode;
  }
  syncDocumentTheme();
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

export function useThemeMode(): [ThemeMode, (nextMode: string) => void] {
  return [useSyncExternalStore(subscribe, readThemeMode, () => 'dark'), setThemeMode];
}
